import React from 'react'

import { IchraAdminSignupWizardState } from 'core'
import {
  AgreeTerms,
  CompanyAddressValues,
  CompanyNameValues,
  PhoneNumberValues,
  TaxClassificationValues,
} from 'core/application/adminSignUp/common/models'
import { GroupHealthPlanPageValues } from 'core/application/adminSignUp/gchra/groupHealthPlan'
// gchra imports
import { GroupHraAdminSignupWizardState } from 'core/application/adminSignUp/gchra/state'
import MakePaymentValues from 'core/application/adminSignUp/makePayment'
// perks imports
import { PerksAdminSignupWizardState } from 'core/application/adminSignUp/perks/state'
import { QsehraAdminSignupWizardState } from 'core/application/adminSignUp/qsehra/state'
import { ProductIds } from 'core/application/commons/config'

//
import useCurrentProduct from '../common/hooks/useCurrentProduct'
import GroupHraAdminSignUp from './gcHra'
import GroupCompanyAddress from './gcHra/companyDetails/GroupCompanyAddress'
import GroupCompanyName from './gcHra/companyDetails/GroupCompanyName'
import GroupHealthPlan from './gcHra/companyDetails/GroupHealthPlan'
import GroupPhoneNumber from './gcHra/companyDetails/GroupPhoneNumber'
import GroupTaxClassification from './gcHra/companyDetails/GroupTaxClassification'
import GroupMakePayment from './gcHra/subscription/GroupMakePayment'
import GroupSubscriptionFees from './gcHra/subscription/GroupSubscriptionFees'
// ichra imports
import IchraAdminSignupWizard from './ichra'
import IchraCompanyAddress from './ichra/companyDetails/IchraCompanyAddress'
import IchraCompanyName from './ichra/companyDetails/IchraCompanyName'
import IchraCompanyNumber from './ichra/companyDetails/IchraCompanyNumber'
import IchraTaxClassification from './ichra/companyDetails/IchraTaxClassification'
import IchraMakePayment from './ichra/subscription/IchraMakePayment'
import IchraSubscriptionFees from './ichra/subscription/IchraSubscriptionFees'
import PerksAdminSignup from './perks'
import PerksCompanyAddress from './perks/companyDetails/PerksCompanyAddress'
import PerksCompanyName from './perks/companyDetails/PerksCompanyName'
import PerksPhoneNumber from './perks/companyDetails/PerksPhoneNumber'
import PerksMakePayment from './perks/subscription/PerksMakePayment'
import PerksSubscriptionFees from './perks/subscription/PerksSubscriptionFees'
// qsehra imports
import QsehraAdminSignupWizard from './qsehra'
import QsehraTaxClassification from './qsehra/QsehraTaxClassification'
import qsehraCompanyDetails from './qsehra/companyDetails'
import qsehraSubscription from './qsehra/subscription'

export const ADMIN_SIGNUP_PATH = '/signup-flow'

const productSignupMap = new Map<number, any>()
productSignupMap.set(ProductIds.GroupHraId, <GroupHraAdminSignUp />)
productSignupMap.set(ProductIds.Perks, <PerksAdminSignup />)
productSignupMap.set(ProductIds.QsehraId, <QsehraAdminSignupWizard />)
productSignupMap.set(ProductIds.IchraId, <IchraAdminSignupWizard />)

const AdminSignup: React.FC = () => {
  const product = useCurrentProduct()
  return productSignupMap.get(product.productId)
}

export default AdminSignup

export enum SectionType {
  COMPANY_DETAILS = 'COMPANY_DETAILS',
  SUBSCRIPTION = 'SUBSCRIPTION',
}

export enum GchraSignupComponentKey {
  COMPANY_NAME = 'gchra-company-name',
  COMPANY_PHONE_NUMBER = 'gchra-company-phone-number',
  COMPANY_ADDRESS = 'gchra-company-address',
  COMPANY_HEALTH_PLAN = 'gchra-company-health-plan',
  TAX_CLASSIFICATION = 'gchra-company-tax-classification',
  SUBSCRIPTION_FEES = 'gchra-subscription-fees',
  SUBSCRIPTION_MAKE_PAYMENT = 'gchra-subscription-make-payment',
}

export enum PerksSignupComponentKey {
  COMPANY_NAME = 'perks-company-name',
  COMPANY_PHONE_NUMBER = 'perks-company-phone-number',
  COMPANY_ADDRESS = 'perks-company-address',
  SUBSCRIPTION_FEES = 'perks-subscription-fees',
  SUBSCRIPTION_MAKE_PAYMENT = 'perks-subscription-make-payment',
}

export enum QsehraSignupComponentKey {
  COMPANY_NAME = 'qsehra-company-name',
  COMPANY_PHONE_NUMBER = 'qsehra-company-phone-number',
  COMPANY_ADDRESS = 'qsehra-company-address',
  TAX_CLASSIFICATION = 'qsehra-company-tax-classification',
  SUBSCRIPTION_FEES = 'qsehra-subscription-fees',
  SUBSCRIPTION_MAKE_PAYMENT = 'qsehra-subscription-make-payment',
}

export enum IchraSignupComponentKey {
  COMPANY_NAME = 'ichra-company-name',
  COMPANY_PHONE_NUMBER = 'ichra-company-phone-number',
  COMPANY_ADDRESS = 'ichra-company-address',
  TAX_CLASSIFICATION = 'ichra-company-tax-classification',
  SUBSCRIPTION_FEES = 'ichra-subscription-fees',
  SUBSCRIPTION_MAKE_PAYMENT = 'ichra-subscription-make-payment',
}

/**
 * IMPORTANT: Order do matters for components within an object with the same "sectionType".
 * In this way is kept track of current active step and the total number of questions in wizard.
 */

export const RoutesMapping = new Map<any, any>([
  [
    ProductIds.GroupHraId,
    [
      {
        key: GchraSignupComponentKey.COMPANY_NAME,
        route: '/company-name',
        component: GroupCompanyName,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: GroupHraAdminSignupWizardState): CompanyNameValues => {
          return {
            companyName: state.companyName,
            ein: state.ein,
            isAle: false,
          }
        },
      },
      {
        key: GchraSignupComponentKey.COMPANY_PHONE_NUMBER,
        route: '/phone-number',
        component: GroupPhoneNumber,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: GroupHraAdminSignupWizardState): PhoneNumberValues => {
          return {
            mainPhoneNumber: state.phoneNumber.mainPhoneNumber !== null ? state.phoneNumber.mainPhoneNumber : '',
            optionalPhoneNumber: state.phoneNumber.workPhoneNumber,
          }
        },
      },
      {
        key: GchraSignupComponentKey.COMPANY_ADDRESS,
        route: '/company-address',
        component: GroupCompanyAddress,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: GroupHraAdminSignupWizardState): CompanyAddressValues => {
          const address = state.address
          return {
            stateId: address.stateId !== null ? address.stateId : '',
            city: address.city !== null ? address.city : '',
            addressLine1: address.addressLine1 !== null ? address.addressLine1 : '',
            zipCode: address.zipCode !== null ? address.zipCode : '',
            addressLine2: address.addressLine2,
          }
        },
      },
      {
        key: GchraSignupComponentKey.COMPANY_HEALTH_PLAN,
        route: '/group-health-plan',
        component: GroupHealthPlan,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: GroupHraAdminSignupWizardState): GroupHealthPlanPageValues => {
          const groupHealthPlan = state.groupHealthPlan
          return {
            id: groupHealthPlan.id,
            groupHealthPlanStartDate: groupHealthPlan.startDate,
            groupHealthPlanName: groupHealthPlan.planName,
            insuranceCarrier: groupHealthPlan.insuranceCarrier,
            waitingPeriod: groupHealthPlan.waitingPeriod,
          }
        },
      },
      {
        key: GchraSignupComponentKey.TAX_CLASSIFICATION,
        route: '/tax-classification',
        component: GroupTaxClassification,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: GroupHraAdminSignupWizardState): TaxClassificationValues => {
          return {
            companyTypeId: state.companyTypeId === null ? 0 : state.companyTypeId,
          }
        },
      },
      {
        key: GchraSignupComponentKey.SUBSCRIPTION_FEES,
        route: '/subscription-summary',
        component: GroupSubscriptionFees,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: GroupHraAdminSignupWizardState): AgreeTerms => {
          return {
            serviceAgreement: state.serviceAgreement,
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: null,
            subscriptionPackageCode: state.subscriptionPackageCode,
            seatNumber: state.seatNumber,
          }
        },
      },
      {
        key: GchraSignupComponentKey.SUBSCRIPTION_MAKE_PAYMENT,
        route: '/make-payment',
        component: GroupMakePayment,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: GroupHraAdminSignupWizardState): MakePaymentValues => {
          return {
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: null,
            seatNumber: state.seatNumber,
          }
        },
      },
    ],
  ],
  [
    ProductIds.Perks,
    [
      {
        key: PerksSignupComponentKey.COMPANY_NAME,
        route: '/company-name',
        component: PerksCompanyName,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: PerksAdminSignupWizardState): CompanyNameValues => {
          return {
            companyName: state.companyName,
            ein: state.ein,
            isAle: false,
          }
        },
      },
      {
        key: PerksSignupComponentKey.COMPANY_PHONE_NUMBER,
        route: '/phone-number',
        component: PerksPhoneNumber,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: PerksAdminSignupWizardState): PhoneNumberValues => {
          return {
            mainPhoneNumber: state.phoneNumber.mainPhoneNumber !== null ? state.phoneNumber.mainPhoneNumber : '',
            optionalPhoneNumber: state.phoneNumber.workPhoneNumber,
          }
        },
      },
      {
        key: PerksSignupComponentKey.COMPANY_ADDRESS,
        route: '/company-address',
        component: PerksCompanyAddress,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: PerksAdminSignupWizardState): CompanyAddressValues => {
          const address = state.address
          return {
            stateId: address.stateId !== null ? address.stateId : '',
            city: address.city !== null ? address.city : '',
            addressLine1: address.addressLine1 !== null ? address.addressLine1 : '',
            zipCode: address.zipCode !== null ? address.zipCode : '',
            addressLine2: address.addressLine2,
          }
        },
      },
      {
        key: PerksSignupComponentKey.SUBSCRIPTION_FEES,
        route: '/subscription-summary',
        component: PerksSubscriptionFees,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: PerksAdminSignupWizardState): any => {
          return {
            serviceAgreement: state.serviceAgreement,
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: state.frequency,
            subscriptionPackageCode: state.subscriptionPackageCode,
            seatNumber: state.seatNumber,
          }
        },
      },
      {
        key: PerksSignupComponentKey.SUBSCRIPTION_MAKE_PAYMENT,
        route: '/make-payment',
        component: PerksMakePayment,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: PerksAdminSignupWizardState): MakePaymentValues => {
          return {
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: state.frequency,
            seatNumber: state.seatNumber,
          }
        },
      },
    ],
  ],
  [
    ProductIds.QsehraId,
    [
      {
        key: QsehraSignupComponentKey.COMPANY_NAME,
        route: '/company-name',
        component: qsehraCompanyDetails.QsehraCompanyName,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: QsehraAdminSignupWizardState): CompanyNameValues => {
          return {
            companyName: state.companyName,
            ein: state.ein,
            isAle: false,
          }
        },
      },
      {
        key: QsehraSignupComponentKey.COMPANY_PHONE_NUMBER,
        route: '/phone-number',
        component: qsehraCompanyDetails.QsehraPhoneNumber,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: QsehraAdminSignupWizardState): PhoneNumberValues => {
          return {
            mainPhoneNumber: state.phoneNumber.mainPhoneNumber !== null ? state.phoneNumber.mainPhoneNumber : '',
            optionalPhoneNumber: state.phoneNumber.workPhoneNumber,
          }
        },
      },
      {
        key: QsehraSignupComponentKey.COMPANY_ADDRESS,
        route: '/company-address',
        component: qsehraCompanyDetails.QsehraCompanyAddress,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: QsehraAdminSignupWizardState): CompanyAddressValues => {
          const address = state.address
          return {
            stateId: address.stateId !== null ? address.stateId : '',
            city: address.city !== null ? address.city : '',
            addressLine1: address.addressLine1 !== null ? address.addressLine1 : '',
            zipCode: address.zipCode !== null ? address.zipCode : '',
            addressLine2: address.addressLine2,
          }
        },
      },
      {
        key: QsehraSignupComponentKey.TAX_CLASSIFICATION,
        route: '/tax-classification',
        component: QsehraTaxClassification,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: QsehraAdminSignupWizardState): TaxClassificationValues => {
          return {
            companyTypeId: state.companyTypeId === null ? 0 : state.companyTypeId,
          }
        },
      },
      {
        key: QsehraSignupComponentKey.SUBSCRIPTION_FEES,
        route: '/subscription-summary',
        component: qsehraSubscription.QsehraSubscriptionFees,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: QsehraAdminSignupWizardState): any => {
          return {
            serviceAgreement: state.serviceAgreement,
            subscriptionPackageCode: state.subscriptionPackageCode,
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: state.frequency,
            seatNumber: state.seatNumber,
          }
        },
      },
      {
        key: QsehraSignupComponentKey.SUBSCRIPTION_MAKE_PAYMENT,
        route: '/make-payment',
        component: qsehraSubscription.QsehraMakePayment,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: QsehraAdminSignupWizardState): MakePaymentValues => {
          return {
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: state.frequency,
            seatNumber: state.seatNumber,
          }
        },
      },
    ],
  ],
  [
    ProductIds.IchraId,
    [
      {
        key: IchraSignupComponentKey.COMPANY_NAME,
        route: '/company-name',
        component: IchraCompanyName,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: IchraAdminSignupWizardState): CompanyNameValues => {
          return {
            companyName: state.companyName,
            ein: state.ein,
            isAle: state.isAle,
          }
        },
      },
      {
        key: IchraSignupComponentKey.COMPANY_PHONE_NUMBER,
        route: '/phone-number',
        component: IchraCompanyNumber,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: IchraAdminSignupWizardState): PhoneNumberValues => {
          return {
            mainPhoneNumber: state.phoneNumber.mainPhoneNumber !== null ? state.phoneNumber.mainPhoneNumber : '',
            optionalPhoneNumber: state.phoneNumber.workPhoneNumber,
          }
        },
      },
      {
        key: IchraSignupComponentKey.COMPANY_ADDRESS,
        route: '/company-address',
        component: IchraCompanyAddress,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: IchraAdminSignupWizardState): CompanyAddressValues => {
          const address = state.address
          return {
            stateId: address.stateId !== null ? address.stateId : '',
            city: address.city !== null ? address.city : '',
            addressLine1: address.addressLine1 !== null ? address.addressLine1 : '',
            zipCode: address.zipCode !== null ? address.zipCode : '',
            addressLine2: address.addressLine2,
          }
        },
      },
      {
        key: IchraSignupComponentKey.TAX_CLASSIFICATION,
        route: '/tax-classification',
        component: IchraTaxClassification,
        sectionType: SectionType.COMPANY_DETAILS,
        mapState: (state: IchraAdminSignupWizardState): TaxClassificationValues => {
          return {
            companyTypeId: state.companyTypeId === null ? 0 : state.companyTypeId,
          }
        },
      },
      {
        key: IchraSignupComponentKey.SUBSCRIPTION_FEES,
        route: '/subscription-summary',
        component: IchraSubscriptionFees,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: IchraAdminSignupWizardState): any => {
          return {
            serviceAgreement: state.serviceAgreement,
            subscriptionPackageCode: state.subscriptionPackageCode,
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: state.frequency,
            seatNumber: state.seatNumber,
          }
        },
      },
      {
        key: IchraSignupComponentKey.SUBSCRIPTION_MAKE_PAYMENT,
        route: '/make-payment',
        component: IchraMakePayment,
        sectionType: SectionType.SUBSCRIPTION,
        mapState: (state: IchraAdminSignupWizardState): MakePaymentValues => {
          return {
            discountCode: state.discountCode,
            planCode: state.planCode,
            frequency: state.frequency,
            seatNumber: state.seatNumber,
          }
        },
      },
    ],
  ],
])
